import { useState } from 'react'
import { AlertTriangle, Undo2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useIssueRefund } from './queries'
import type { RefundPayload } from './types'
import { formatMoneyCents } from './format'

// A refund-aware "Partial −$X" / "Refunded" badge. Renders nothing
// when the payment hasn't been refunded.
export function RefundStatusBadge({
  refundedCents,
  grossCents,
  currency,
}: {
  refundedCents: number
  grossCents: number
  currency: string
}) {
  if (refundedCents <= 0) return null
  const fully = refundedCents >= grossCents
  return (
    <Badge variant={fully ? 'danger' : 'warning'} className="ml-1">
      {fully ? 'Refunded' : `Partial −${formatMoneyCents(refundedCents, currency)}`}
    </Badge>
  )
}

export interface RefundablePayment {
  id: number
  invoiceNumber: string | null
  customerName: string | null
  grossCents: number
  refundedCents: number
  currency: string
  method: string
}

// Inline "Refund" button on a payment row. Self-contained: opens the
// modal, fires the mutation, surfaces toasts. Hidden when the payment
// is already fully refunded or isn't a Stripe charge.
export function RefundActionButton({ payment }: { payment: RefundablePayment }) {
  const { hasPermission } = useAuth()
  const canRefund = hasPermission('invoicing.refunds.create')
  const [open, setOpen] = useState(false)
  const remainingCents = payment.grossCents - payment.refundedCents
  if (!canRefund) return null
  if (payment.method !== 'stripe') return null
  if (remainingCents <= 0) return null
  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2 text-xs"
        onClick={() => setOpen(true)}
      >
        <Undo2 className="size-3" /> Refund
      </Button>
      <RefundModal open={open} onOpenChange={setOpen} payment={payment} />
    </>
  )
}

export function RefundModal({
  open,
  onOpenChange,
  payment,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  payment: RefundablePayment
}) {
  const { toast } = useToast()
  const issueRefund = useIssueRefund()
  const remainingCents = payment.grossCents - payment.refundedCents
  const [amount, setAmount] = useState<string>((remainingCents / 100).toFixed(2))
  const [reasonCode, setReasonCode] = useState<RefundPayload['reasonCode']>('requested_by_customer')
  const [note, setNote] = useState<string>('')
  const [postToAccounts, setPostToAccounts] = useState(true)

  const onSubmit = async () => {
    const amt = Number(amount)
    if (!Number.isFinite(amt) || amt <= 0) {
      toast({ variant: 'destructive', title: 'Amount must be greater than zero.' })
      return
    }
    if (amt * 100 > remainingCents + 0.5) {
      toast({
        variant: 'destructive',
        title: 'Exceeds refundable balance',
        description: `Maximum refundable is ${formatMoneyCents(remainingCents, payment.currency)}.`,
      })
      return
    }
    try {
      await issueRefund.mutateAsync({
        paymentId: payment.id,
        amount: amt,
        reasonCode,
        reason: note.trim() || undefined,
        postToAccounts,
      })
      toast({
        title: 'Refund issued',
        description: `${formatMoneyCents(Math.round(amt * 100), payment.currency)} sent back to the customer's card.`,
      })
      onOpenChange(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Refund failed',
        description: err instanceof Error ? err.message : 'unknown',
      })
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Refund payment"
      className="max-w-lg"
    >
      <div className="space-y-3">
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-semibold">
                {payment.invoiceNumber ? `Invoice ${payment.invoiceNumber}` : 'Stripe payment'}
              </div>
              <div className="text-xs text-muted-foreground">{payment.customerName ?? '-'}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Refundable</div>
              <div className="font-mono font-semibold">
                {formatMoneyCents(remainingCents, payment.currency)}
              </div>
            </div>
          </div>
        </div>
        <FormField
          id="refund-amount"
          label={`Amount (${payment.currency})`}
          hint="Defaults to the full remaining balance. Lower it for a partial refund."
          control={
            <Input
              id="refund-amount"
              type="number"
              min={0}
              step={0.01}
              value={amount}
              onChange={(e) => setAmount(e.currentTarget.value)}
              inputMode="decimal"
            />
          }
        />
        <FormField
          id="refund-reason-code"
          label="Reason"
          control={
            <Select
              value={reasonCode || 'other'}
              onValueChange={(v) => setReasonCode(v as RefundPayload['reasonCode'])}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="requested_by_customer">Requested by customer</SelectItem>
                <SelectItem value="duplicate">Duplicate charge</SelectItem>
                <SelectItem value="fraudulent">Fraudulent</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          }
        />
        <FormField
          id="refund-note"
          label="Internal note (optional)"
          hint="Shown on the dashboard; not sent to the customer."
          control={
            <Input
              id="refund-note"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
            />
          }
        />
        <label className="flex items-start gap-2 rounded-md border p-3 text-sm">
          <Checkbox
            checked={postToAccounts}
            onCheckedChange={(v) => setPostToAccounts(v === true)}
            className="mt-0.5"
          />
          <span className="flex-1">
            <span className="font-medium">Post to accounting</span>
            <span className="block text-xs text-muted-foreground">
              Default. Records a Dr AR / Cr Cash journal entry so reports reflect the refund.
              Uncheck if the refund is purely a card-side reversal that shouldn&apos;t hit the GL
              (e.g. a duplicate charge that never made it into your books).
            </span>
          </span>
        </label>
        <Alert>
          <AlertTriangle className="size-4" />
          <AlertDescription>
            Stripe&apos;s processing fee is <strong>not</strong> returned. The card holder
            gets back the refund amount; whoever paid the Stripe fee originally
            absorbs that loss.
          </AlertDescription>
        </Alert>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={issueRefund.isPending} variant="danger">
            <Undo2 className="size-4" />
            {issueRefund.isPending ? 'Refunding…' : 'Issue refund'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
